import { ColumnDef } from "@tanstack/react-table";
import useDataTable from "hooks/useDataTable";
import ActionButton from "Components/atoms/buttons/action-button";
import Chip from "Components/atoms/chip";
import withRouter from "Components/Common/withRouter";
import DataTableTemplate from "Components/templates/data-table-template";
import SearchGeneratedDocsForm from "Components/organisms/forms/search-generated-docs-form";
import { SelectOption } from "Components/molecule/inputs/select-input";
import useManual from "hooks/useManual"
import GenDocStatus, { GEN_DOC_STATUS, GEN_DOC_STATUS_VARIANT } from "models/gen-doc-status";
import GeneratedDoc, { IGeneratedDocQueryParams } from "models/generated-doc";
import Doc from "models/document";
import Employee from "models/employee";
import React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { PermissionsSliceState } from "slices/auth/permissions/reducer";
import { ProfileSliceState } from "slices/auth/profile/reducer";
import { DocumentGeneratorState, setGeneratedDocCount, setGeneratedDocs, setIsLoadingGeneratedDocs } from "slices/human-resources/document-generator/reducer";
import { getGeneratedDocuments, getGeneratedDocsSearchFields } from "slices/human-resources/document-generator/thunk";
import { navigateToApprover, navigateToConsultantAgreement, navigateToGenerator, navigateToSearchDocuments } from "utils/navigator";
import { formatDate, formatDateTimeAmPm } from "utils/tools";
import { PlusIcon, HelpIcon } from "Components/atoms/icons";

const APPROVER_CODE = 'SC_HR_AP_008'
const GENERATOR_CODE = 'SC_HR_DG_006'

const DEFAULT_QUERY: IGeneratedDocQueryParams = {
    page: 0,
    limit: Number.MAX_SAFE_INTEGER,
    statusId: "",
    documentId: "",
    managerId: "",
    recruitName: "",
}

const selectDocumentGenerator = createSelector(
    (state: any) => state.DocumentGenerator,
    ({
        generatedDocs,
        isLoadingGenratedDocs,
        generatedDocCount
    }: DocumentGeneratorState) => ({
        generatedDocs,
        isLoadingGenratedDocs,
        generatedDocCount
    })
)

const selectPermissions = createSelector(
    (state: any) => state.Permissions,
    ({ screens }: PermissionsSliceState) => ({
        screens
    })
)

const selectProfile = createSelector(
    (state: any) => state.Profile,
    ({ employee }: ProfileSliceState) => ({
        employee
    })
)

// Display name used to sort the approver columns
const employeeName = (e?: Employee | null): string | undefined =>
    e ? `${e.commonName ?? e.firstName} ${e.lastName}` : undefined

// Each approver's own chip status — independent of the document's overall status.
const approverStatus = (approvedAt?: Date | string | null): GEN_DOC_STATUS =>
    approvedAt ? GEN_DOC_STATUS.APPROVE : GEN_DOC_STATUS.PENDING

const DocumentsPage = (props) => {
    document.title = "Generated Docs | IbexIQ";
    const { handleOpenManual } = useManual("human-resources/document-generator");
    const dispatch: any = useDispatch();

    const [query, setQuery] = useState<IGeneratedDocQueryParams>(DEFAULT_QUERY)

    const [documents, setDocuments] = useState<Doc[]>([])
    const [managers, setManagers] = useState<Employee[]>([])
    const [statuses, setStatuses] = useState<GenDocStatus[]>([])

    const {
        generatedDocs,
        isLoadingGenratedDocs,
        generatedDocCount
    } = useSelector(selectDocumentGenerator);
    const { screens } = useSelector(selectPermissions);
    const { employee } = useSelector(selectProfile);

    const fetchGeneratedDocs = useCallback(async (q: IGeneratedDocQueryParams) => {
        dispatch(setIsLoadingGeneratedDocs(true))

        const result = await getGeneratedDocuments(q)
        if (result) {
            dispatch(setGeneratedDocs(result.data))
            dispatch(setGeneratedDocCount(result.count))
        }

        dispatch(setIsLoadingGeneratedDocs(false))
    }, [dispatch])

    useEffect(() => {
        const init = async () => {
            const fields = await getGeneratedDocsSearchFields()
            if (fields) {
                setDocuments(fields.documents)
                setManagers(fields.managers)
                setStatuses(fields.statuses)
            }

            if (generatedDocs.length === 0) {
                await fetchGeneratedDocs(query)
            }
        }
        init()
    }, [])



    const isGenerator = useMemo(() => screens.some(s => s.code === GENERATOR_CODE), [screens])

    const handleChangeQuery = <K extends keyof IGeneratedDocQueryParams>(
        key: K,
        value: IGeneratedDocQueryParams[K]
    ) => {
        const q = {
            ...query,
            ...(key !== 'page' && key !== 'limit' ? { page: 0, limit: Number.MAX_SAFE_INTEGER } : {}),
            [key]: value
        }

        // Search fields wait for Search.
        setQuery(q)
    };

    const handleSearch = useCallback(() => {
        fetchGeneratedDocs(query)
    }, [query, fetchGeneratedDocs])

    const handleClearSearch = useCallback(() => {
        setQuery(DEFAULT_QUERY)
        fetchGeneratedDocs(DEFAULT_QUERY)
    }, [fetchGeneratedDocs])

    const documentOptions: SelectOption[] = useMemo(() => documents.map(d => ({
        value: d.id, label: d.name
    })), [documents])

    const managerOptions: SelectOption[] = useMemo(() => managers.map((m: Employee) => ({
        value: m.id, label: `${m.commonName ?? m.firstName} ${m.lastName}`
    })), [managers])

    const statusOptions: SelectOption[] = useMemo(() => statuses.map((s: GenDocStatus) => ({
        value: s.id, label: s.description
    })), [statuses])


    const columns = React.useMemo<ColumnDef<GeneratedDoc>[]>(() => [
        {
            header: "Document Name",
            id: "documentName",
            accessorFn: (row) => row.document?.name,
            size: 175,
            meta: {
                getCellStyle: (value: number) => {
                    return {
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                    }
                },
            },
            cell: ({ row }) => {
                return <a
                    style={{ color: "var(--bs-table-link-color)" }}
                    onClick={() => handleNavigateToDocumentDetailsPage(row.original)}
                >{row.original.document?.name}</a>
            }
        },
        {
            header: "Employee Name",
            id: "employeeName",
            accessorFn: (row) => {
                const recruit = row.genDocRecruits?.[0]
                return recruit ? (recruit.commonName ?? `${recruit.firstName} ${recruit.lastName}`) : undefined
            },
            size: 150,
            cell: ({ row }) => {
                const recruit = row.original.genDocRecruits?.[0]
                return recruit ? (recruit.commonName ?? `${recruit.firstName} ${recruit.lastName}`) : '—'
            }
        },
        {
            header: "HR Admin",
            id: "hrAdmin",
            accessorFn: (row) => employeeName(row.hrAppr),
            size: 150,
            cell: ({ row }) => {
                const isRejected = row.original.status?.description === GEN_DOC_STATUS.REJECT
                const status = approverStatus(row.original.hrApprovedAt)
                return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {row.original.hrAppr && `${row.original.hrAppr?.commonName ?? row.original.hrAppr?.firstName} ${row.original.hrAppr?.lastName}`}
                    {!isRejected && <Chip variant={GEN_DOC_STATUS_VARIANT[status]}>{status}</Chip>}
                </span>
            }
        },
        {
            header: "Manager",
            id: "manager",
            accessorFn: (row) => employeeName(row.managerAppr),
            size: 150,
            cell: ({ row }) => {
                const isRejected = row.original.status?.description === GEN_DOC_STATUS.REJECT
                const status = approverStatus(row.original.managerApprovedAt)
                return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {row.original.managerAppr?.commonName ?? row.original.managerAppr?.firstName} {row.original.managerAppr?.lastName}
                    {!isRejected && <Chip variant={GEN_DOC_STATUS_VARIANT[status]}>{status}</Chip>}
                </span>
            }
        },
        {
            header: "Higher Manager",
            id: "higherManager",
            accessorFn: (row) => row.higherManagerApproverId ? employeeName(row.higherMgrAppr) : undefined,
            size: 150,
            cell: ({ row }) => {
                if (!row.original.higherManagerApproverId) return 'Not Found'

                const isRejected = row.original.status?.description === GEN_DOC_STATUS.REJECT
                const status = approverStatus(row.original.higherManagerApprovedAt)
                return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {row.original.higherMgrAppr?.commonName ?? row.original.higherMgrAppr?.firstName} {row.original.higherMgrAppr?.lastName}
                    {!isRejected && <Chip variant={GEN_DOC_STATUS_VARIANT[status]}>{status}</Chip>}
                </span>
            }
        },
        {
            header: "President",
            id: "president",
            accessorFn: (row) => row.presidentApproverId ? employeeName(row.presidentAppr) : undefined,
            size: 150,
            cell: ({ row }) => `${row.original.presidentApproverId ? `${row.original.presidentAppr?.commonName ?? row.original.presidentAppr?.firstName} ${row.original.presidentAppr?.lastName} ${row.original.presidentApprovedAt ? '✅' : `${row.original.status?.description === GEN_DOC_STATUS.PENDING ? '🕒' : ''}`}` : 'No'}`,
        },
        {
            header: "Status",
            id: "status",
            accessorFn: (row) => row.status?.description,
            size: 100,
            cell: ({ row }) => {
                const status = row.original.status?.description
                if (!status) return <Chip>Not found</Chip>

                return <Chip variant={GEN_DOC_STATUS_VARIANT[status]}>{status}</Chip>
            },
        },
        {
            header: "Submitted At",
            id: "submittedAt",
            accessorFn: (row) => row.submittedAt ? new Date(row.submittedAt).getTime() : undefined,
            size: 100,
            cell: ({ row }) => formatDateTimeAmPm(row.original.submittedAt)
        },
        {
            header: "Created",
            id: "createdAt",
            accessorFn: (row) => row.createdAt ? new Date(row.createdAt).getTime() : undefined,
            size: 120,
            cell: ({ row }) => formatDate(row.original.createdAt as any)
        },
        {
            header: "Last Updated",
            id: "updatedAt",
            accessorFn: (row) => row.updatedAt ? new Date(row.updatedAt).getTime() : undefined,
            size: 120,
            cell: ({ row }) => formatDate(row.original.updatedAt as any)
        }

    ], [employee]);

    const handleCreateNewDocument = useCallback(() => {
        navigateToSearchDocuments(props.router.navigate)
    }, [props.router.navigate])

    const handleNavigateToDocumentDetailsPage = useCallback((gDoc: GeneratedDoc) => {
        if (gDoc.document?.code === 'CONSULTANT_AGREEMENT') {
            navigateToConsultantAgreement(props.router.navigate, gDoc.documentId, gDoc.id)
        } else {
            navigateToGenerator(props.router.navigate, gDoc.documentId, gDoc.id)
        }
    }, [props.router.navigate])



    const table = useDataTable({
        columns,
        data: generatedDocs,
        // President is rarely used: hidden until the user shows it (Columns button)
        initialColumnVisibility: { president: false },
        initialSorting: [{ id: 'submittedAt', desc: true }],
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    return <DataTableTemplate
        title={"Human Resources"}
        breadcrumbItem={"Agreement Generator"}
        table={table}
        columns={columns}
        isLoading={isLoadingGenratedDocs}
        rowCount={generatedDocCount}
        filters={<SearchGeneratedDocsForm
            handleChangeQuery={handleChangeQuery}
            handleClearSearch={handleClearSearch}
            handleSearch={handleSearch}
            documents={documentOptions}
            managers={managerOptions}
            statuses={statusOptions}
            query={query}
            isSearching={isLoadingGenratedDocs}
        />}
        actions={
            <>

                {
                    isGenerator && <ActionButton onClick={handleCreateNewDocument}>
                        Create <PlusIcon />
                    </ActionButton>
                }
                {
                    <ActionButton
                        onClick={handleOpenManual}
                    >
                        Help <HelpIcon />
                    </ActionButton>
                }
            </>
        }
    />
}


export default withRouter(DocumentsPage);
